import { uiFb } from '@/lib/i18n';

interface LanguageAlternate {
  lang: string;
  slug: string;
  label: string;
  labelEn?: string;
}

interface LanguageLinksProps {
  alternates: LanguageAlternate[];
  currentLang: string;
}

/**
 * Crawlable language switcher for calendar (MadLibs) pages.
 *
 * The interactive MadLibs language picker navigates via `window.location`
 * (JS-only), so search engines can't follow it — which orphaned every
 * non-English country/city language variant (they had `<head>` hreflang but
 * no crawlable inbound `<a>`). This renders real `<a href>` links so each
 * language version has a crawlable inbound edge. (almanak-220z.8)
 */
export function LanguageLinks({ alternates, currentLang }: LanguageLinksProps) {
  const others = alternates.filter(a => a.slug && a.lang !== currentLang);
  if (others.length === 0) return null;

  const label = uiFb('ui.select_language', 'Select language', currentLang);

  return (
    <nav aria-label={label} className="mt-8 border-t border-border-subtle pt-4">
      <span className="mr-2 text-xs font-medium uppercase tracking-wide text-text-secondary">
        {label}
      </span>
      <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
        {others.map(alt => (
          <li key={alt.lang}>
            <a
              href={`/${alt.slug}`}
              hrefLang={alt.lang}
              className="text-sm text-text-secondary hover:text-text-primary hover:underline"
            >
              {alt.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
